<script lang="ts" setup>
/* Notification List Item Component
 *
 * This component renders a single notification item with a title, message, time, and a menu to toggle read status.
 */
import { computed } from 'vue';
import { formatTime } from '@/v1/utils/dateformatter';
import Popper from 'vue3-popper';

const props = defineProps<{
    id: string;
    title: string;
    message: string;
    timestamp: number;
    initialUnread: boolean;
    target?: string;
}>();

const emit = defineEmits<{
    (e: 'toggleRead', id: string, newUnreadState: boolean): void
    (e: 'click', id: string, target?: string): void
    (e: 'delete', id: string): void
}>();

const displayTime = computed(() => {
    return formatTime(props.timestamp);
});

const handleToggleRead = (closePopper?: () => void) => {
    emit('toggleRead', props.id, !props.initialUnread);
    if (closePopper) closePopper();
};

const handleDelete = (closePopper?: () => void) => {
    emit('delete', props.id);
    if (closePopper) closePopper();
};
</script>

<template>
    <div @click="emit('click', props.id, props.target)" :data-notification-id="props.id"
        class="panel lan:p-2 lan:relative lan:flex lan:flex-row lan:cursor-pointer lan:select-none lan:transition-colors group"
        :class="{ 'lan:bg-blue-50': props.initialUnread, 'lan:bg-white lan:hover:bg-gray-50': !props.initialUnread }">

        <div class="lan:flex-1 lan:flex lan:flex-col lan:gap-1 lan:min-w-0  lan:no-underline lan:text-inherit"
            :class="{ 'lan:cursor-pointer': !!props.target }">
            <div class="lan:font-medium lan:text-gray-900 lan:truncate"
                :class="{ 'lan:font-bold': props.initialUnread }">
                {{ props.title }}
            </div>
            <div class="lan:text-sm lan:text-gray-600 lan:line-clamp-2 lan:wrap-break-word">
                {{ props.message }}
            </div>
        </div>

        <div class="lan:flex lan:flex-col lan:items-end lan:justify-start lan:gap-1">
            <div class="lan:text-xs lan:text-gray-500 lan:whitespace-nowrap lan:select-none">
                {{ displayTime }}
            </div>
            <div @click.stop>
                <Popper placement="left-start" offsetDistance="0">
                    <button
                        class="lan:w-6 lan:h-6 lan:rounded-full lan:transition-colors lan:flex lan:items-center lan:justify-center lan:text-gray-500 lan:bg-transparent lan:outline-none lan:border-none lan:cursor-pointer lan:hover:bg-gray-200">
                        <i class="fa-solid fa-ellipsis-vertical lan:text-sm"></i>
                    </button>
                    <template #content="{ close }">
                        <div
                            class="lan:bg-white lan:rounded-md lan:shadow-lg lan:border lan:border-gray-200 lan:py-1 lan:min-w-[180px]">
                            <button @click.prevent.stop="handleToggleRead(close)"
                                class="lan:w-full lan:text-left lan:px-4 lan:py-2 lan:text-sm lan:text-[#008bd2] lan:hover:bg-transparent lan:flex lan:items-center lan:gap-2 lan:border-none lan:outline-none lan:bg-transparent lan:cursor-pointer">
                                <i :class="props.initialUnread ? 'fa-regular fa-envelope-open' : 'fa-regular fa-envelope'"
                                    class="lan:w-4"></i>
                                {{ props.initialUnread ? 'Als gelesen markieren' : 'Als ungelesen markieren' }}
                            </button>
                            <button @click.prevent.stop="handleDelete(close)"
                                class="lan:w-full lan:text-left lan:px-4 lan:py-2 lan:text-sm lan:text-red-600 lan:hover:bg-red-50 lan:flex lan:items-center lan:gap-2 lan:border-none lan:outline-none lan:bg-transparent lan:cursor-pointer">
                                <i class="fa-regular fa-trash-can lan:w-4"></i>
                                Löschen
                            </button>
                        </div>
                    </template>
                </Popper>
            </div>
        </div>
    </div>
</template>